import { LanguageType } from 'types';
import { useLanguage } from 'features/hooks/useLanguage';
import { useAlerts } from 'hooks/ui';
import { ApiError } from 'features/invite-users/useProjectInvitations';
import { useProjectUnfreeze } from './useProjectUnfreeze';
import { Button } from 'features/components/Button';
import { Dialog } from 'features/entry-form-v2/component/Dialog';

interface Props {
	projectId: string;
	projectName: string;
	onClose: () => void;
	visible: boolean;
}

export function UnfreezeProjectModal({ projectId, projectName, onClose, visible }: Props) {
	const language = useLanguage();
	const dictionary = DICTIONARY[language];
	const { setNotification, setError } = useAlerts();

	const { unfreezeProject, isUnfreezing } = useProjectUnfreeze();

	const handleSubmit = async () => {
		try {
			await unfreezeProject({ projectId: parseInt(projectId, 10) });
			setNotification({ message: dictionary.unfreezeSuccess });
			onClose();
		} catch (error) {
			const apiError = error as ApiError;
			setError({
				message: apiError?.message || dictionary.unfreezeFail
			});
		}
	};

	return (
		<Dialog open={visible} onClose={onClose} title={dictionary.modalTitle} hideCloseButton>
			<div className="space-y-6 w-[512px]">
				<div className="bg-gray-50 p-4 rounded-lg space-y-4">
					<p className="text-base text-gray-700">
						{dictionary.descriptionText}{' '}
						<span className="font-medium text-gray-900">{projectName}</span>
					</p>
				</div>

				<div className="flex flex-wrap justify-end gap-3 pt-4 border-t-2 border-gray-200">
					<Button
						text={dictionary.cancelButton}
						onClick={onClose}
						variant="secondary"
						disabled={isUnfreezing}
					/>
					<Button
						text={dictionary.unfreezeButton}
						onClick={handleSubmit}
						variant="primary"
						loading={isUnfreezing}
					/>
				</div>
			</div>
		</Dialog>
	);
}

const DICTIONARY = {
	[LanguageType.English]: {
		modalTitle: 'Unfreeze Project',
		descriptionText: 'You are about to unfreeze project:',
		cancelButton: 'Cancel',
		unfreezeButton: 'Unfreeze Project',
		unfreezeSuccess: 'Project unfrozen successfully.',
		unfreezeFail: 'Failed to unfreeze project. Please try again later.',
		somethingWentWrong: 'Something went wrong. Please try again later.'
	},
	[LanguageType.Norwegian]: {
		modalTitle: 'Opphev frysing av prosjekt',
		descriptionText: 'Du er i ferd med å oppheve frysing av prosjektet:',
		cancelButton: 'Avbryt',
		unfreezeButton: 'Opphev frysing',
		unfreezeSuccess: 'Frysing av prosjektet er opphevet.',
		unfreezeFail: 'Kunne ikke oppheve frysing av prosjektet. Vennligst prøv igjen senere.',
		somethingWentWrong: 'Noe gikk galt. Vennligst prøv igjen senere.'
	}
} as const;
